import React from 'react';
import {
  ArrayResolution,
  ConditionType,
  Filter,
  filterOperatorOptions,
  getRelationalExpressionProps,
  RelationsProps,
  supportParserOperatorOptions,
} from '@fabric-ui/components';
import { emptyObject, generateOption } from '@fabric-ui/utils';
import { FieldType } from '../../../views/Sources/Mapping/types';
import DateTypeBox from '../DateTypeBox';
import { CustomFieldHandler, useExpressionBuilder } from '../ExpressionBuilderContext';
import { FieldFilterBoxSelect } from '../FieldFilterBoxSelect';
import { useFilterConfig, useFilterContext } from '../FilterContext';
import FilterTypeBox from '../FilterTypeBox';
import { FromToFilterModeValueInput } from './FromToFilterModeValueInput';
import { TokenSuggestion } from './TokenFilterInput/types';

interface ValueInputSectionProps {
  actualFilterValue: Filter;
  autoFocus: boolean;
  enableFilter: boolean;
  fieldName?: string | null;
  filter: Filter;
  isOperatorLockedByToken: boolean;
  setEffectiveFilter: (filter: Filter) => void;
  setFilter: (filter: Filter) => void;
  showListTab: boolean;
  typeCondition: ConditionType;
  typeName: string;
}

export const ValueInputSection: React.FC<ValueInputSectionProps> = React.memo(
  ({
    actualFilterValue,
    autoFocus,
    enableFilter,
    fieldName,
    filter,
    isOperatorLockedByToken,
    setEffectiveFilter,
    setFilter,
    showListTab,
    typeCondition,
    typeName,
  }) => {
    const {
      customFieldHandlers = emptyObject as Record<string, CustomFieldHandler>,
      fieldExtraOptions,
      operatorOptions = filterOperatorOptions,
      readOnly = false,
      relationsProps = emptyObject as RelationsProps,
      supportParser = false,
      tokenSuggestions,
    } = useExpressionBuilder();
    const { disabled, isVertical, size, mode } = useFilterConfig();
    const { accountEntities } = useFilterContext();
    const fieldMap = accountEntities?.fieldMap ?? {};

    const customHandler = fieldName ? customFieldHandlers[fieldName] : undefined;
    if (customHandler?.component) {
      const CustomComponent = customHandler.component;
      return (
        <CustomComponent
          actualFilterValue={actualFilterValue}
          autoFocus={autoFocus}
          disabled={disabled}
          enableFilter={enableFilter}
          fieldName={fieldName}
          filter={filter}
          isOperatorLockedByToken={isOperatorLockedByToken}
          isVertical={isVertical}
          readOnly={readOnly}
          setEffectiveFilter={setEffectiveFilter}
          setFilter={setFilter}
          showListTab={showListTab}
          size={size}
          typeCondition={typeCondition}
          typeName={typeName}
        />
      );
    }

    const innerExpression = actualFilterValue.expression?.arrayCondition?.underlying || actualFilterValue.expression;
    const currentConditionValue =
      innerExpression?.[typeCondition] && Object.values(innerExpression[typeCondition]).find(value => typeof value === 'string');
    const isInvalidToken =
      typeof currentConditionValue === 'string' &&
      currentConditionValue.startsWith('{') &&
      currentConditionValue.endsWith('}') &&
      !tokenSuggestions?.some(token => token.value === currentConditionValue);
    const setValueWithOperator = (token: TokenSuggestion) => {
      const [tokenOperator, tokenResolution] = token.operator.split('|') as [string, ArrayResolution | undefined];
      const underlying = {
        fieldName: innerExpression?.fieldName,
        [typeCondition]: { [tokenOperator]: token.value },
        ...getRelationalExpressionProps({
          operator: tokenOperator,
          typeCondition,
          enableCaseSensitive: !!relationsProps.enableCaseSensitive,
          enableSide: !!relationsProps.enableSide,
        }),
      };
      const existingArrayCondition = actualFilterValue.expression?.arrayCondition;
      const useArrayCondition = tokenResolution || existingArrayCondition;
      setEffectiveFilter({
        ...actualFilterValue,
        expression: useArrayCondition
          ? { arrayCondition: { underlying, resolution: (tokenResolution ?? existingArrayCondition?.resolution) as ArrayResolution } }
          : underlying,
      });
    };

    // From-to filter mode: render from/to operator + value pairs
    if (mode === 'fromToFilter' && !showListTab) {
      return (
        <FromToFilterModeValueInput
          actualFilterValue={actualFilterValue}
          enableFilter={enableFilter}
          disabled={disabled}
          readOnly={readOnly}
          fieldName={fieldName}
          fieldMap={fieldMap}
          typeCondition={typeCondition}
          typeName={typeName}
          operatorOptions={operatorOptions}
          size={size as 'small' | 'xSmall' | 'medium'}
          setEffectiveFilter={setEffectiveFilter}
        />
      );
    }

    const isEmptyDate = !!actualFilterValue.expression?.stringCondition?.empty;
    const isNotEmptyDate = !!actualFilterValue.expression?.stringCondition?.notEmpty;
    const isDateFilter = !!fieldName && [FieldType.Date, FieldType.DateTime].includes(fieldMap[fieldName]?.type as string);
    const shouldRenderOperator = !(isDateFilter && (isEmptyDate || isNotEmptyDate));

    if (!shouldRenderOperator || typeCondition === ConditionType.date) {
      return <DateTypeBox filter={actualFilterValue} setFilter={setEffectiveFilter} />;
    }

    if (showListTab) {
      return (
        <FieldFilterBoxSelect
          fieldName={fieldName}
          value={filter}
          onChange={setEffectiveFilter}
          extraOptions={fieldName ? fieldExtraOptions?.[fieldName] : undefined}
          disabled={!enableFilter || disabled}
        />
      );
    }

    if (!typeName) return <div>Not Implemented</div>;

    return (
      <FilterTypeBox
        typeCondition={typeCondition}
        options={supportParser ? supportParserOperatorOptions.string : operatorOptions[typeName] || []}
        filter={actualFilterValue}
        setFilter={setEffectiveFilter}
        disabled={!enableFilter || disabled}
        relationsProps={
          typeCondition === ConditionType.enum
            ? { ...relationsProps, additionalSelectOptions: fieldMap[fieldName!]?.originalType.values?.map(generateOption) }
            : relationsProps
        }
        resetOperatorOnChange={isOperatorLockedByToken}
        filterInputProps={{
          onTokenSelect: setValueWithOperator,
          error: isInvalidToken ? 'Invalid dynamic variable' : undefined,
        }}
      />
    );
  }
);
